// Restricted arithmetic parser: API text is never executed as JavaScript.
export const PRICING_VARIABLES = ['List Price', 'Sum of Options', 'SmallLaborMultiplier', 'SmallLaborAdder', 'SmallOverheadMultiplier', 'SmallNetMultiplier', 'Setup Multiplier Amount', 'Setup Multiplier Percent'];
const canonical = name => name.trim().replace(/\s+/g, ' ').toLowerCase();
export function parsePricingExpression(formula) {
  if (typeof formula !== 'string' || !formula.trim()) throw new Error('Pricing formula is missing.');
  const source = formula.trim().replace(/^=/, '').trim();
  if (source.length > 4000) throw new Error('Pricing formula exceeds 4000 characters.');
  const tokens = [];
  let offset = 0;
  while (offset < source.length) {
    const match = /^(?:\s+|(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|[A-Za-z][A-Za-z ]*|[()+\-*/])/.exec(source.slice(offset));
    if (!match) throw new Error(`Unsupported formula character at position ${offset + 1}.`);
    offset += match[0].length;
    if (match[0].trim()) tokens.push(match[0].trim());
  }
  let index = 0;
  let depth = 0;
  const primary = () => {
    if (++depth > 100) throw new Error('Pricing formula is nested too deeply.');
    const token = tokens[index++];
    let node;
    if (token === '+' || token === '-') node = { op: token, right: primary() };
    else if (token === '(') {
      node = expression();
      if (tokens[index++] !== ')') throw new Error('Pricing formula is missing a closing parenthesis.');
    } else if (token && /^(?:\d|\.)/.test(token)) {
      if (!Number.isFinite(Number(token))) throw new Error('Formula numbers must be finite.');
      node = { number: Number(token) };
    } else {
      const variable = PRICING_VARIABLES.find(name => canonical(name) === canonical(token || ''));
      if (!variable) throw new Error(`Unknown variable or missing value: ${token || 'end of formula'}.`);
      node = { variable };
    }
    depth--;
    return node;
  };
  const product = () => {
    let node = primary();
    while (['*', '/'].includes(tokens[index])) node = { op: tokens[index++], left: node, right: primary() };
    return node;
  };
  const expression = () => {
    let node = product();
    while (['+', '-'].includes(tokens[index])) node = { op: tokens[index++], left: node, right: product() };
    return node;
  };
  const tree = expression();
  if (index !== tokens.length) throw new Error(`Unexpected formula token: ${tokens[index]}. Check operators and parentheses.`);
  return tree;
}
export function evaluatePricingExpression(formula, variables) {
  const visit = node => {
    if ('number' in node) return node.number;
    if (node.variable) {
      const raw = variables[node.variable];
      if (raw == null || String(raw).trim() === '' || !Number.isFinite(Number(raw))) throw new Error(`${node.variable} is unavailable.`);
      return Number(raw);
    }
    const right = visit(node.right);
    const left = node.left ? visit(node.left) : 0;
    if (node.op === '/' && right === 0) throw new Error('Pricing formula cannot divide by zero.');
    const value = node.op === '+' ? left + right : node.op === '-' ? left - right : node.op === '*' ? left * right : left / right;
    if (!Number.isFinite(value)) throw new Error('Pricing formula produced a non-finite result.');
    return value;
  };
  return visit(parsePricingExpression(formula));
}

// Solve a * SmallNetMultiplier + b = target. Reject nonlinear expressions
// rather than guessing a price or silently choosing one of several solutions.
export function solvePricingMultiplier(formula, variables, target) {
  if (String(target).trim() === '' || !Number.isFinite(Number(target)) || Number(target) <= 0) throw new Error('Enter a desired sale price greater than zero.');
  const visit = node => {
    if ('number' in node) return [0, node.number];
    if (node.variable) return node.variable === 'SmallNetMultiplier' ? [1, 0] : [0, evaluatePricingExpression(node.variable, variables)];
    const [a, b] = node.left ? visit(node.left) : [0, 0];
    const [c, d] = visit(node.right);
    if (node.op === '+') return [a + c, b + d];
    if (node.op === '-') return [a - c, b - d];
    if (node.op === '*') {
      if (a !== 0 && c !== 0) throw new Error('Compare Price requires a formula linear in SmallNetMultiplier.');
      return [a * d + b * c, b * d];
    }
    if (c !== 0) throw new Error('Compare Price cannot solve a formula with SmallNetMultiplier in a divisor.');
    if (d === 0) throw new Error('Pricing formula cannot divide by zero.');
    return [a / d, b / d];
  };
  const [a, b] = visit(parsePricingExpression(formula));
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) throw new Error('This formula does not have a usable multiplier adjustment.');
  const multiplier = (Number(target) - b) / a;
  if (!Number.isFinite(multiplier) || multiplier <= 0) throw new Error('That price cannot be reached with a positive multiplier.');
  return multiplier;
}
